import React from "react";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  LocationCity as LocationCityIcon,
  AccountBalance as AccountBalanceIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  Warehouse as WarehouseIcon,
  LocalMall as LocalMallIcon,
  SwapHoriz as SwapHorizIcon,
  TableBar as TableBarIcon,
  RestaurantMenu as RestaurantMenuIcon,
  Assessment as AssessmentIcon,
  Nfc as NfcIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as RolesIcon,
  Business as BusinessIcon,
  Keyboard as KeyboardIcon,
  WarningAmber as WarningAmberIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import { getTradeTerminology } from "../utils/tradeTerminology";

export type AdminNavLeaf = {
  kind: "leaf";
  id: string;
  text: string;
  icon: React.ReactNode;
  path: string;
  module: string;
  requireRecharge?: boolean;
};

export type AdminNavGroup = {
  kind: "group";
  id: string;
  text: string;
  icon: React.ReactNode;
  children: AdminNavLeaf[];
};

export type AdminNavEntry = AdminNavLeaf | AdminNavGroup;

export type AdminNavSection = {
  id: string;
  label: string;
  entries: AdminNavEntry[];
};

/** Paths that are admin-only report screens (hidden for field staff). */
const ADMIN_REPORT_PATHS = new Set([
  "/reports",
  "/reports/sales",
  "/reports/gst-books",
  "/reports/chart-of-accounts",
  "/reports/journals",
  "/reports/inventory-alerts",
]);

export function isAdminReportPath(path: string): boolean {
  return ADMIN_REPORT_PATHS.has(path);
}

export type BuildAdminNavOptions = {
  isSuperAdmin: boolean;
  isPlatformAdmin: boolean;
  hasAnyRole: (...roles: string[]) => boolean;
  businessType?: string | null;
};

/**
 * Flat sidebar by default. Only NFC stays as a submenu (related card/wallet flows).
 * Sales / POS lives in the header, not here.
 */
export function buildAdminNavSections({
  isSuperAdmin,
  isPlatformAdmin,
  hasAnyRole,
  businessType,
}: BuildAdminNavOptions): AdminNavSection[] {
  const normalizedType = String(businessType || "ALL_IN_ONE").toUpperCase();
  const isNonRestaurant =
    normalizedType === "PHARMACY" ||
    normalizedType === "GROCERY" ||
    normalizedType === "WHOLESALE";

  const showRestaurantFeatures =
    isSuperAdmin ||
    (!isNonRestaurant &&
      (normalizedType === "RESTAURANT" ||
        normalizedType === "ALL_IN_ONE" ||
        (!businessType && isPlatformAdmin)));

  const terms = getTradeTerminology(businessType);
  const isOperator = hasAnyRole(
    "OPERATOR",
    "SALES_POINT_ADMIN",
    "MERCHANT",
    "GROCERY_STORE"
  );

  const productListLabel = isOperator
    ? `Sales Point ${terms.itemPlural}`
    : terms.productListLabel;

  const tablesLabel = isOperator
    ? `Sales Point ${terms.tablesLabel}`
    : terms.tablesLabel;

  const setupEntries: AdminNavEntry[] = [];

  if (isPlatformAdmin) {
    setupEntries.push({
      kind: "leaf",
      id: "companies",
      text: "Companies",
      icon: <BusinessIcon />,
      path: "/companies",
      module: "companies",
    });
  }

  setupEntries.push(
    {
      kind: "leaf",
      id: "branches",
      text: "Branches",
      icon: <LocationCityIcon />,
      path: "/branches",
      module: "branches",
    },
    {
      kind: "leaf",
      id: "sales-points",
      text: "Sales Points",
      icon: <AccountBalanceIcon />,
      path: "/sales-points",
      module: "sales_points",
    },
    {
      kind: "leaf",
      id: "users",
      text: "Users",
      icon: <PeopleIcon />,
      path: "/users",
      module: "users",
    }
  );

  if (isSuperAdmin || isPlatformAdmin) {
    setupEntries.push({
      kind: "leaf",
      id: "roles",
      text: "Roles",
      icon: <RolesIcon />,
      path: "/roles",
      module: "permissions",
    });
  }

  const catalogEntries: AdminNavEntry[] = [
    {
      kind: "leaf",
      id: "categories",
      text: terms.categoryNavText,
      icon: <CategoryIcon />,
      path: "/categories",
      module: "categories",
    },
    {
      kind: "leaf",
      id: "products-master",
      text: terms.productMasterLabel,
      icon: <InventoryIcon />,
      path: "/product-master",
      module: "products",
    },
    {
      kind: "leaf",
      id: "product-list",
      text: productListLabel,
      icon: <InventoryIcon />,
      path: "/product-list",
      module: "product_sales_points",
    },
    {
      kind: "leaf",
      id: "purchases",
      text: terms.purchasesLabel,
      icon: <LocalMallIcon />,
      path: "/purchases",
      module: "purchases",
    },
    {
      kind: "leaf",
      id: "suppliers",
      text: terms.suppliersLabel,
      icon: <PeopleIcon />,
      path: "/suppliers",
      module: "suppliers",
    },
    ...(showRestaurantFeatures
      ? [
          {
            kind: "leaf" as const,
            id: "recipes",
            text: terms.recipesLabel,
            icon: <RestaurantMenuIcon />,
            path: "/recipes",
            module: "recipes",
          },
          {
            kind: "leaf" as const,
            id: "tables",
            text: tablesLabel,
            icon: <TableBarIcon />,
            path: "/tables",
            module: "tables",
          },
        ]
      : []),
    {
      kind: "leaf",
      id: "customers",
      text: terms.customersLabel,
      icon: <PeopleIcon />,
      path: "/customers",
      module: "customers",
    },
  ];

  /** Only real submenu: NFC card + wallet flows stay together. */
  const nfcChildren: AdminNavLeaf[] = [
    {
      kind: "leaf",
      id: "cardholders",
      text: "Card Holders",
      icon: <CreditCardIcon />,
      path: "/cardholders",
      module: "cardholders",
    },
    {
      kind: "leaf",
      id: "nfc-cards",
      text: "NFC Cards",
      icon: <NfcIcon />,
      path: "/nfc-cards",
      module: "nfc_cards",
    },
    {
      kind: "leaf",
      id: "check-balance",
      text: "Check Balance",
      icon: <AccountBalanceWalletIcon />,
      path: "/check-balance",
      module: "nfc_cards",
    },
    {
      kind: "leaf",
      id: "quick-recharge",
      text: "Quick Recharge",
      icon: <PaymentIcon />,
      path: "/quick-recharge",
      module: "recharges",
    },
    {
      kind: "leaf",
      id: "recharges",
      text: "Recharges",
      icon: <AccountBalanceIcon />,
      path: "/recharges",
      module: "recharges",
    },
    {
      kind: "leaf",
      id: "transactions",
      text: "Transactions",
      icon: <ReceiptIcon />,
      path: "/transactions",
      module: "transactions",
    },
  ];

  const reportEntries: AdminNavEntry[] = [
    {
      kind: "leaf",
      id: "reports-hub",
      text: "Reports Hub",
      icon: <AssessmentIcon />,
      path: "/reports",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "reports-sales",
      text: terms.salesReportLabel,
      icon: <AssessmentIcon />,
      path: "/reports/sales",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "reports-inventory-alerts",
      text: "Inventory Alerts",
      icon: <WarningAmberIcon />,
      path: "/reports/inventory-alerts",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "reports-gst-books",
      text: "GST Books",
      icon: <ReceiptIcon />,
      path: "/reports/gst-books",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "reports-chart-of-accounts",
      text: "Chart of Accounts",
      icon: <AccountBalanceIcon />,
      path: "/reports/chart-of-accounts",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "reports-journals",
      text: "Journals",
      icon: <ReceiptIcon />,
      path: "/reports/journals",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "vouchers",
      text: "Vouchers",
      icon: <ReceiptIcon />,
      path: "/vouchers",
      module: "vouchers",
    },
    {
      kind: "leaf",
      id: "my-reports",
      text: "My Reports",
      icon: <AssessmentIcon />,
      path: "/my-reports",
      module: "reports",
    },
  ];

  const systemEntries: AdminNavEntry[] = [
    {
      kind: "leaf",
      id: "shortcuts",
      text: "Shortcut",
      icon: <KeyboardIcon />,
      path: "/shortcuts",
      module: "dashboard",
    },
    {
      kind: "leaf",
      id: "settings",
      text: "Settings",
      icon: <SettingsIcon />,
      path: "/settings",
      module: "settings",
    },
  ];

  if (isSuperAdmin) {
    systemEntries.push({
      kind: "leaf",
      id: "audit-logs",
      text: "Audit logs",
      icon: <AssessmentIcon />,
      path: "/audit-logs",
      module: "audit_logs",
    });
  }

  return [
    {
      id: "overview",
      label: "Overview",
      entries: [
        {
          kind: "leaf",
          id: "dashboard",
          text: "Dashboard",
          icon: <DashboardIcon />,
          path: "/dashboard",
          module: "dashboard",
        },
      ],
    },
    {
      id: "setup",
      label: "Setup",
      entries: setupEntries,
    },
    {
      id: "catalog",
      label: "Catalog & sales",
      entries: catalogEntries,
    },
    {
      id: "warehouse",
      label: isOperator ? "Stock & Inventory" : "Warehouse",
      entries: [
        {
          kind: "leaf",
          id: "warehouses",
          text: "Warehouses",
          icon: <WarehouseIcon />,
          path: "/warehouses",
          module: "warehouses",
        },
        {
          kind: "leaf",
          id: "warehouse-stock",
          text: terms.stockOutletLabel,
          icon: <InventoryIcon />,
          path: "/inventory",
          module: "inventory",
        },
        {
          kind: "leaf",
          id: "stock-transfers",
          text: "Stock transfers",
          icon: <SwapHorizIcon />,
          path: "/stock-transfers",
          module: "stock_transfers",
        },
      ],
    },
    {
      id: "nfc",
      label: "Cards & wallet",
      entries: nfcChildren,
    },
    {
      id: "reports",
      label: "Reports",
      entries: reportEntries,
    },
    {
      id: "system",
      label: "System",
      entries: systemEntries,
    },
  ];
}

export type AdminNavFilterContext = {
  isSuperAdmin: boolean;
  isPlatformAdmin: boolean;
  isAdminOrAbove?: boolean;
  isFieldStaff: boolean;
  showQuickRecharge: boolean;
  canRead: (module: string) => boolean;
};

export function isNavLeafVisible(
  leaf: AdminNavLeaf,
  ctx: AdminNavFilterContext
): boolean {
  if (leaf.path === "/companies") {
    return ctx.isPlatformAdmin;
  }
  if (ctx.isSuperAdmin) {
    return true;
  }
  if (leaf.path === "/roles") {
    return ctx.canRead("permissions") || ctx.isSuperAdmin;
  }
  if (leaf.path === "/audit-logs") {
    return ctx.canRead("audit_logs") || ctx.isSuperAdmin;
  }
  if (leaf.path === "/product-master") {
    return (
      ctx.canRead("products") ||
      Boolean(ctx.isAdminOrAbove ?? (ctx.isSuperAdmin || ctx.isPlatformAdmin))
    );
  }
  if (leaf.path === "/stock-transfers") {
    return (
      ctx.canRead("stock_transfers") ||
      (ctx.canRead("inventory") && !ctx.isFieldStaff)
    );
  }
  if (!ctx.canRead(leaf.module)) return false;
  if (leaf.requireRecharge && !ctx.showQuickRecharge) return false;
  if (leaf.path === "/my-reports") return ctx.isFieldStaff;
  if (isAdminReportPath(leaf.path)) return !ctx.isFieldStaff;
  return true;
}

function filterGroup(
  group: AdminNavGroup,
  ctx: AdminNavFilterContext
): AdminNavGroup | null {
  const children = group.children.filter((c) => isNavLeafVisible(c, ctx));
  if (!children.length) return null;
  return { ...group, children };
}

/** Filter sections/entries by RBAC; drop empty groups and sections. */
export function filterAdminNavSections(
  sections: AdminNavSection[],
  ctx: AdminNavFilterContext
): AdminNavSection[] {
  return sections
    .map((section) => {
      const entries = section.entries
        .map((entry) => {
          if (entry.kind === "leaf") {
            return isNavLeafVisible(entry, ctx) ? entry : null;
          }
          return filterGroup(entry, ctx);
        })
        .filter(Boolean) as AdminNavEntry[];
      if (!entries.length) return null;
      return { ...section, entries };
    })
    .filter(Boolean) as AdminNavSection[];
}

/** Flat leaf list for search + active-route matching. */
export function flattenAdminNavLeaves(
  sections: AdminNavSection[]
): AdminNavLeaf[] {
  const leaves: AdminNavLeaf[] = [];
  for (const section of sections) {
    for (const entry of section.entries) {
      if (entry.kind === "leaf") leaves.push(entry);
      else leaves.push(...entry.children);
    }
  }
  return leaves;
}

/** Groups whose child matches pathname should stay expanded. */
export function groupsContainingPath(
  sections: AdminNavSection[],
  pathname: string
): string[] {
  const ids: string[] = [];
  for (const section of sections) {
    for (const entry of section.entries) {
      if (entry.kind !== "group") continue;
      const hit = entry.children.some(
        (c) => pathname === c.path || pathname.startsWith(`${c.path}/`)
      );
      if (hit) ids.push(entry.id);
    }
  }
  return ids;
}
